import { useState, useEffect, useRef } from 'react'
import { Card, CardBody, CardHeader, Chip } from '@heroui/react'
import { Icon } from '@iconify/react'
import { Button } from '@/components/ui/button'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { EngineAiOutput, StreamingTextActionDto } from '@interfaces/ai'
import { ai } from '@/services/ai/ai-service'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import ApplyImprovementBtn from './apply-improvement-btn'
import { highlightManager } from '@/utils/highlightManager'
import { cn } from '@/lib/utils'
import { getRiskFactorProperties } from './RiskAnalysisResults'

interface RiskCardProps {
  risk: EngineAiOutput['riskCards'][number]
  versionNumber: number
  documentSummary: string
  onStatusChange: (newCard: EngineAiOutput['riskCards'][number]) => void
  isFocused?: boolean
  riskIndex?: number
  onCardClick?: (index: number) => void
  reviewInProgress?: boolean
}

// Dots component to show risk factor visually
const RiskFactorDots = ({ riskFactor }: { riskFactor: number }) => {
  const { dotColor } = getRiskFactorProperties(riskFactor)

  return (
    <div className="ml-2 flex items-center gap-0.5">
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} className={`h-2 w-2 rounded-full ${i < riskFactor ? dotColor : 'bg-gray-200 dark:bg-gray-600'}`} />
      ))}
    </div>
  )
}

const handleScrollOnClick = (blockId: string) => {
  const block = document.querySelector(`[data-block-id="${blockId}"]`)
  if (!block) return

  block.scrollIntoView({ behavior: 'smooth', block: 'center' })

  document.querySelectorAll('.highlighted-block-new').forEach(el => {
    el.classList.remove('highlighted-block-new', 'highlighted-block-new-sweep')
  })

  let lastScrollTop = document.documentElement.scrollTop || document.body.scrollTop

  const checkScrollEnd = () => {
    const currentScrollTop = document.documentElement.scrollTop || document.body.scrollTop

    if (lastScrollTop === currentScrollTop) {
      block.classList.add('highlighted-block-new', 'highlighted-block-new-sweep')
    } else {
      lastScrollTop = currentScrollTop
      requestAnimationFrame(checkScrollEnd)
    }
  }

  requestAnimationFrame(checkScrollEnd)
}

const RiskCard = ({
  risk,
  versionNumber,
  documentSummary,
  onStatusChange,
  isFocused,
  riskIndex,
  onCardClick,
  reviewInProgress,
}: RiskCardProps) => {
  const [enhancedContent, setEnhancedContent] = useState<string | undefined>(risk.enhancedText)
  const [isStreaming, setIsStreaming] = useState(false)
  const { id: documentPublicId } = useParams()
  const cardRef = useRef<HTMLDivElement>(null)

  // Register this card with the global highlight manager
  useEffect(() => {
    highlightManager.registerCard(cardRef)

    return () => {
      highlightManager.unregisterCard(cardRef)
    }
  }, [])

  const toggleContent = () => {
    // If currently showing enhanced content, revert to original
    if (enhancedContent) {
      setEnhancedContent(undefined)
    } else {
      // If currently showing original and we have enhanced text available, show enhanced
      setEnhancedContent(risk.enhancedText)
    }
  }

  const startStreamingTextAction = (
    action: 'risk_card_simplify' | 'risk_card_elaborate_more',
    e?: React.MouseEvent
  ) => {
    if (!documentPublicId) return

    setIsStreaming(true)
    setEnhancedContent('')

    // Check if test mode is activated
    const url = new URL(window.location.href)
    const testMode = url.searchParams.get('test') === 'true'
    // --------------------------------------------

    const payload: StreamingTextActionDto = {
      action,
      documentVersionNumber: versionNumber,
      documentPublicId: documentPublicId,
      riskCard: risk,
      testMode,
    }

    ai.streamingTextAction(
      payload,
      (event: string, data: { fullText?: string; finalText?: string; error?: string }) => {
        if (event === 'error') {
          setIsStreaming(false)
          setEnhancedContent(data.error || 'An error occurred while processing your request.')
        } else if (event === 'stream_start') {
          setEnhancedContent('')
          setIsStreaming(true)
        } else if (event === 'text_chunk') {
          setEnhancedContent(data.fullText || '')
        } else if (event === 'stream_end') {
          setIsStreaming(false)
          if (data.finalText) {
            setEnhancedContent(data.finalText)
            onStatusChange({ ...risk, enhancedText: data.finalText })
          }
        }
      },
      (error: any) => {
        setIsStreaming(false)
        toast.error(error?.response?.data?.error ?? 'Something went wrong while processing your request.')
        setEnhancedContent(undefined)
      }
    )
  }

  return (
    <Card
      ref={cardRef}
      key={risk.blockId}
      onPress={() => {
        // Remove native focus from any element
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur()

        handleScrollOnClick(risk.blockId)
        if (riskIndex !== undefined && onCardClick) onCardClick(riskIndex)
      }}
      isPressable
      tabIndex={-1}
      data-risk-card-id={risk.blockId}
      data-risk-index={riskIndex}
      className={cn(
        'grid animate-enter overflow-hidden bg-white p-2 text-start focus:outline-none focus-visible:outline-none',
        isFocused && 'outline outline-2 outline-offset-1 outline-[rgba(255,215,0,0.6)]'
      )}
    >
      <CardHeader className="flex-col items-start justify-start">
        <div className="mb-3 flex w-full items-center gap-2">
          <Chip
            className="high-risk-tab font-medium"
            variant="flat"
            color={getRiskFactorProperties(risk.riskFactor).color}
          >
            <span className="font-medium">{getRiskFactorProperties(risk.riskFactor).label}</span>
          </Chip>

          {/* risk factor dots from 1-10 */}
          {getRiskFactorProperties(risk.riskFactor).level !== 'INFORMATIONAL' && (
            <RiskFactorDots riskFactor={risk.riskFactor} />
          )}

          {/* extra options popover three vertical dots */}
          {!reviewInProgress && (
            <div className="ms-auto flex gap-1">
              {/* toggle content button */}
              {!!risk.enhancedText && !isStreaming && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 rounded-full bg-blue-100 px-2 text-blue-700 hover:bg-blue-200"
                  onClick={e => {
                    e.stopPropagation()
                    toggleContent()
                  }}
                >
                  <Icon
                    icon={enhancedContent ? 'material-symbols:undo-rounded' : 'material-symbols:auto-awesome-rounded'}
                    width={16}
                    height={16}
                    className="mr-1"
                  />
                  {enhancedContent ? 'See original' : 'See enhanced'}
                </Button>
              )}

              {/* enhance options popover */}
              {!enhancedContent && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 rounded-full bg-default/40 p-0 hover:bg-default/60"
                      onClick={e => e.stopPropagation()}
                    >
                      <Icon icon="simple-line-icons:options" width={18} height={18} className="text-gray-600" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        startStreamingTextAction('risk_card_simplify', e)
                      }}
                      disabled={isStreaming || !!enhancedContent}
                    >
                      <Icon icon="material-symbols:compress-rounded" width={16} height={16} className="mr-2" />
                      Make concise
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        startStreamingTextAction('risk_card_elaborate_more', e)
                      }}
                      disabled={isStreaming || !!enhancedContent}
                    >
                      <Icon icon="material-symbols:expand-content-rounded" width={16} height={16} className="mr-2" />
                      Elaborate more
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>

        <div className="text-small text-default-600">
          {isStreaming && !enhancedContent ? (
            <div className="flex items-center gap-2 py-2">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-500"></div>
              <span className="text-gray-600">Generating enhanced explanation...</span>
            </div>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: !!enhancedContent ? enhancedContent : `${risk.what} ${risk.why}`,
              }}
            />
          )}
        </div>
      </CardHeader>

      {risk.suggestion && (
        <CardBody className="suggested-response">
          <Card radius="sm" className="whitespace-normal bg-default/40" shadow="none">
            <CardHeader className="flex pb-2 text-sm">
              <Icon icon="stash:light-bulb" />
              <span className="me-auto ml-1 text-default-600">Suggested action</span>

              {!reviewInProgress && (
                <div className="flex gap-1">
                  {/* Status: PENDING - Show both Apply and Dismiss */}
                  {(!risk.status || risk.status === 'PENDING') && (
                    <>
                      <ApplyImprovementBtn
                        versionNumber={versionNumber}
                        risk={risk}
                        documentSummary={documentSummary}
                        onApply={appliedRisk => onStatusChange(appliedRisk)}
                        riskIndex={riskIndex}
                      />

                      {/* Hide dismiss button for informational cards */}
                      {getRiskFactorProperties(risk.riskFactor).level !== 'INFORMATIONAL' && (
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="light"
                                className="px-1"
                                onClick={e => {
                                  e.stopPropagation()
                                  const dismissedRisk = { ...risk, status: 'DISMISSED' as const }
                                  onStatusChange(dismissedRisk)
                                }}
                              >
                                <Icon icon="material-symbols:close-rounded" width={20} height={20} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <span>Dismiss</span>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </>
                  )}

                  {/* Status: DISMISSED - Show Back to Pending */}
                  {risk.status === 'DISMISSED' && (
                    <Button
                      size="sm"
                      variant="light"
                      className="flex gap-0.5"
                      onClick={e => {
                        e.stopPropagation()
                        const pendingRisk = { ...risk, status: 'PENDING' as const }
                        onStatusChange(pendingRisk)
                      }}
                    >
                      <Icon icon="material-symbols:undo-rounded" width={20} height={20} />
                      Back to pending
                    </Button>
                  )}

                  {/* Status: APPLIED - Show Apply Improvement (current behavior) */}
                  {risk.status === 'APPLIED' && (
                    <ApplyImprovementBtn
                      versionNumber={versionNumber}
                      risk={risk}
                      documentSummary={documentSummary}
                      onApply={appliedRisk => onStatusChange(appliedRisk)}
                      riskIndex={riskIndex}
                    />
                  )}
                </div>
              )}
            </CardHeader>
            <CardBody className="pt-0 text-sm">
              <div
                dangerouslySetInnerHTML={{
                  __html: risk.suggestion,
                }}
              />
            </CardBody>
          </Card>
        </CardBody>
      )}
    </Card>
  )
}

export default RiskCard
